const CASEDB_CSRF_STORAGE_KEY = 'casedb_csrf_token';
const CASEDB_DEV_SESSION_STORAGE_KEY = 'casedb_dev_session_token';
const CASEDB_DEV_SESSION_BRIDGE_ATTEMPT_KEY = 'casedb_dev_session_bridge_attempt';
const CASEDB_DEV_SESSION_BRIDGE_RETRY_MS = 5 * 60 * 1000;
export const CASEDB_DEV_SESSION_REQUIRED_EVENT = 'casedb:dev-session-required';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const CASEDB_CSRF_HEADER = 'X-CSRF-Token';
const CASEDB_DEV_SESSION_HEADER = 'X-CaseDB-Dev-Session';
const FETCH_PATCH_MARKER = '__CASEDB_CSRF_FETCH_PATCHED__';
const XHR_PATCH_MARKER = '__CASEDB_CSRF_XHR_PATCHED__';
let csrfBootstrapPromise = null;
let csrfTokenRevision = 0;

const isCaseDbApiRequest = (input) => {
  if (typeof window === 'undefined') return false;

  try {
    const url = input instanceof Request ? new URL(input.url, window.location.href) : new URL(String(input), window.location.href);
    return url.hostname === 'api.casedb.co' || url.pathname.startsWith('/api/') || url.pathname.startsWith('/tracking/') || url.pathname.startsWith('/mail/');
  } catch (error) {
    return false;
  }
};

const getRequestMethod = (input, init = {}) => {
  if (init?.method) return String(init.method || 'GET').toUpperCase();
  if (input instanceof Request) return String(input.method || 'GET').toUpperCase();
  return 'GET';
};

export const isCaseDbLocalDevelopmentOrigin = () => {
  if (typeof window === 'undefined') return false;
  return /^https?:\/\/localhost(?::\d+)?$/.test(window.location.origin);
};

export const setCaseDbDevSessionToken = (token) => {
  const normalized = typeof token === 'string' ? token.trim() : '';
  if (normalized) {
    window.__CASEDB_DEV_SESSION_TOKEN = normalized;
    try {
      window.sessionStorage?.setItem(CASEDB_DEV_SESSION_STORAGE_KEY, normalized);
    } catch (error) {
      // Session storage can be unavailable in strict browser modes.
    }
  }
};

export const clearCaseDbDevSessionToken = () => {
  window.__CASEDB_DEV_SESSION_TOKEN = '';
  try {
    window.sessionStorage?.removeItem(CASEDB_DEV_SESSION_STORAGE_KEY);
  } catch (error) {
    // Session storage can be unavailable in strict browser modes.
  }
};

export const getCaseDbDevSessionToken = () => {
  if (typeof window === 'undefined' || !isCaseDbLocalDevelopmentOrigin()) return '';
  const memoryToken = typeof window.__CASEDB_DEV_SESSION_TOKEN === 'string' ? window.__CASEDB_DEV_SESSION_TOKEN.trim() : '';
  if (memoryToken) return memoryToken;
  try {
    const stored = window.sessionStorage?.getItem(CASEDB_DEV_SESSION_STORAGE_KEY) || '';
    return typeof stored === 'string' ? stored.trim() : '';
  } catch (error) {
    return '';
  }
};

const rememberDevSessionBridgeAttempt = () => {
  const attemptedAt = Date.now();
  window.__CASEDB_DEV_SESSION_BRIDGE_ATTEMPT = attemptedAt;
  try {
    window.sessionStorage?.setItem(CASEDB_DEV_SESSION_BRIDGE_ATTEMPT_KEY, String(attemptedAt));
  } catch (error) {
    // The returned URL token also establishes this guard if storage is unavailable.
  }
};

const hasRecentDevSessionBridgeAttempt = () => {
  let attemptedAt = Number(window.__CASEDB_DEV_SESSION_BRIDGE_ATTEMPT || 0);
  try {
    attemptedAt = Math.max(attemptedAt, Number(window.sessionStorage?.getItem(CASEDB_DEV_SESSION_BRIDGE_ATTEMPT_KEY) || 0));
  } catch (error) {
    // The in-memory guard still deduplicates concurrent responses.
  }
  return attemptedAt > 0 && Date.now() - attemptedAt < CASEDB_DEV_SESSION_BRIDGE_RETRY_MS;
};

export const consumeCaseDbDevSessionFromLocation = () => {
  if (typeof window === 'undefined' || !isCaseDbLocalDevelopmentOrigin()) return false;
  const hash = window.location.hash || '';
  if (!hash.includes('casedb_dev_session=')) return false;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('casedb_dev_session') || '';
  params.delete('casedb_dev_session');

  if (token) {
    rememberDevSessionBridgeAttempt();
    setCaseDbDevSessionToken(token);
  }

  const nextHash = params.toString();
  const nextUrl = `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ''}`;
  window.history.replaceState(window.history.state, document.title, nextUrl || '/');
  return Boolean(token);
};

export const startCaseDbDevSessionBridge = ({ userInitiated = false } = {}) => {
  if (typeof window === 'undefined' || !isCaseDbLocalDevelopmentOrigin()) return false;
  if (!userInitiated && hasRecentDevSessionBridgeAttempt()) {
    window.dispatchEvent(new CustomEvent(CASEDB_DEV_SESSION_REQUIRED_EVENT));
    return false;
  }
  // Persist before navigating so a failed round trip cannot restart itself.
  rememberDevSessionBridgeAttempt();
  const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.assign(`https://api.casedb.co/dev-session-bridge.php?return_to=${encodeURIComponent(returnTo)}`);
  return true;
};

const applyCaseDbDevSessionHeader = (headers) => {
  const token = getCaseDbDevSessionToken();
  if (token && !headers.has(CASEDB_DEV_SESSION_HEADER)) {
    headers.set(CASEDB_DEV_SESSION_HEADER, token);
  }
};

const responseHasInvalidDevSession = async (response) => {
  if (!response || response.status !== 401 || !isCaseDbLocalDevelopmentOrigin()) {
    return false;
  }

  try {
    const payload = await response.clone().json();
    return payload?.error === 'Invalid local development session'
      || ['malformed-token', 'signature-mismatch', 'expired-token', 'session-mismatch', 'invalid-token'].includes(String(payload?.reason || ''));
  } catch (error) {
    return false;
  }
};

const recoverInvalidDevSession = async (response, requestDevSessionToken) => {
  if (!requestDevSessionToken || requestDevSessionToken !== getCaseDbDevSessionToken()) {
    return response;
  }
  if (!(await responseHasInvalidDevSession(response))) {
    return response;
  }
  // Another request may have renewed or cleared the token while JSON was read.
  if (requestDevSessionToken !== getCaseDbDevSessionToken()) return response;

  clearCaseDbDevSessionToken();
  clearCaseDbCsrfToken();
  startCaseDbDevSessionBridge();
  return response;
};

export const ensureCaseDbCsrfToken = async ({ forceRefresh = false, expectedUserId = null } = {}) => {
  const currentToken = getCaseDbCsrfToken();
  if (currentToken && !forceRefresh) {
    return currentToken;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  if (!csrfBootstrapPromise) {
    const requestRevision = csrfTokenRevision;
    // One session read serves a concurrent upload batch. Do not turn a late
    // response into a new token after logout or another session update.
    csrfBootstrapPromise = (async () => {
      try {
        const response = await window.fetch(`https://api.casedb.co/session.php?time=${Date.now()}`, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        });
        const payload = await response.json().catch(() => ({}));
        const token = typeof payload?.csrf_token === 'string' ? payload.csrf_token.trim() : '';
        if (!response.ok || payload?.isLoggedIn !== true || !token || csrfTokenRevision !== requestRevision
            || (expectedUserId !== null && String(payload.user_id) !== String(expectedUserId))) {
          return null;
        }
        setCaseDbCsrfToken(token);
        return { token, userId: payload.user_id, revision: csrfTokenRevision };
      } catch (error) {
        return null;
      }
    })().finally(() => { csrfBootstrapPromise = null; });
  }
  const refreshed = await csrfBootstrapPromise;
  if (refreshed && refreshed.revision === csrfTokenRevision
      && (expectedUserId === null || String(refreshed.userId) === String(expectedUserId))) {
    return refreshed.token;
  }
  // A forced refresh must never report the rejected cached token as renewed.
  return forceRefresh ? '' : getCaseDbCsrfToken();
};

export const setCaseDbCsrfToken = (token) => {
  const normalized = typeof token === 'string' ? token.trim() : '';
  if (normalized) {
    csrfTokenRevision += 1;
    window.__CASEDB_CSRF_TOKEN = normalized;
    try {
      window.sessionStorage?.setItem(CASEDB_CSRF_STORAGE_KEY, normalized);
    } catch (error) {
      // Session storage can be unavailable in strict browser modes.
    }
  }
};

export const clearCaseDbCsrfToken = () => {
  csrfTokenRevision += 1;
  window.__CASEDB_CSRF_TOKEN = '';
  try {
    window.sessionStorage?.removeItem(CASEDB_CSRF_STORAGE_KEY);
  } catch (error) {
    // Session storage can be unavailable in strict browser modes.
  }
};

export const getCaseDbCsrfToken = () => {
  if (typeof window === 'undefined') return '';
  const memoryToken = typeof window.__CASEDB_CSRF_TOKEN === 'string' ? window.__CASEDB_CSRF_TOKEN.trim() : '';
  if (memoryToken) return memoryToken;
  try {
    const stored = window.sessionStorage?.getItem(CASEDB_CSRF_STORAGE_KEY) || '';
    return typeof stored === 'string' ? stored.trim() : '';
  } catch (error) {
    return '';
  }
};

export const withCsrfHeaders = (headers = {}, method = 'GET') => {
  const nextHeaders = { ...(headers || {}) };
  const normalizedMethod = String(method || 'GET').toUpperCase();
  const token = getCaseDbCsrfToken();
  if (token && !SAFE_METHODS.has(normalizedMethod)) {
    nextHeaders[CASEDB_CSRF_HEADER] = token;
  }
  return nextHeaders;
};

export const installCaseDbCsrfFetchInterceptor = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  if (window.fetch[FETCH_PATCH_MARKER]) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  const patchedFetch = async (input, init = {}) => {
    const normalizedMethod = getRequestMethod(input, init);
    const isApiRequest = isCaseDbApiRequest(input);

    if (isApiRequest) {
      const headers = input instanceof Request ? new Headers(input.headers) : new Headers(init.headers || {});
      applyCaseDbDevSessionHeader(headers);

      if (!SAFE_METHODS.has(normalizedMethod)) {
        const token = await ensureCaseDbCsrfToken();
        if (token && !headers.has(CASEDB_CSRF_HEADER)) {
          headers.set(CASEDB_CSRF_HEADER, token);
        }
      }

      if (input instanceof Request) {
        const response = await originalFetch(new Request(input, {
          credentials: init.credentials || 'include',
          ...init,
          headers,
        }));
        return recoverInvalidDevSession(response, headers.get(CASEDB_DEV_SESSION_HEADER));
      }

      const response = await originalFetch(input, {
        credentials: init.credentials || 'include',
        ...init,
        headers,
      });
      return recoverInvalidDevSession(response, headers.get(CASEDB_DEV_SESSION_HEADER));
    }

    return originalFetch(input, init);
  };

  patchedFetch[FETCH_PATCH_MARKER] = true;
  window.fetch = patchedFetch;

  if (typeof window.XMLHttpRequest === 'function' && !window.XMLHttpRequest.prototype[XHR_PATCH_MARKER]) {
    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
      this.__CASEDB_XHR_REQUEST = { method: String(method || 'GET').toUpperCase(), url };
      return originalOpen.call(this, method, url, ...rest);
    };

    window.XMLHttpRequest.prototype.send = function patchedSend(body) {
      const request = this.__CASEDB_XHR_REQUEST || {};
      if (request.url && isCaseDbApiRequest(request.url)) {
        const devSessionToken = getCaseDbDevSessionToken();
        if (devSessionToken) {
          this.setRequestHeader(CASEDB_DEV_SESSION_HEADER, devSessionToken);
        }

        const csrfToken = getCaseDbCsrfToken();
        if (csrfToken && !SAFE_METHODS.has(request.method || 'GET')) {
          this.setRequestHeader(CASEDB_CSRF_HEADER, csrfToken);
        }
      }

      return originalSend.call(this, body);
    };

    window.XMLHttpRequest.prototype[XHR_PATCH_MARKER] = true;
  }
};
